import { useCallback, useEffect, useRef, useState } from "react";
import {
  ReactFlow, ReactFlowProvider, Background, Controls, MiniMap,
  useNodesState, useEdgesState, addEdge,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { Plus, Trash } from "@phosphor-icons/react";
import {
  BusNode, edgeStyle, NODE_STYLE, SidePanel, Toolbar, ToolbarBtn, ToolbarDivider,
} from "./flow/primitives";

/* v2.94 · Studies v2 · Graphical single-line editor for LOAD FLOW.
   Refactored to use shared flow/primitives and a proper side-panel
   editor (replaces the earlier window.prompt UX). Emits {buses,
   lines} tables matching the /api/studies/load-flow schema. */

const SlackBus = (p) => (
  <BusNode {...p} {...NODE_STYLE.SLACK}
    sub={(d) => `${d.kV} kV · V=${d.V_pu} pu (swing)`} />
);
const PQBus = (p) => (
  <BusNode {...p} {...NODE_STYLE.PQ}
    sub={(d) => `${d.kV} kV · P=${d.P_mw} MW · Q=${d.Q_mvar} MVAr`} />
);
const PVBus = (p) => (
  <BusNode {...p} {...NODE_STYLE.PV}
    sub={(d) => `${d.kV} kV · V=${d.V_pu} pu · P=${d.P_mw} MW`} />
);
const nodeTypes = { SLACK: SlackBus, PQ: PQBus, PV: PVBus };

const DEFAULT_NODES = [
  { id: "B1", type: "SLACK", position: { x: 60, y: 120 },
    data: { id: "B1", kV: 13.8, type: "SLACK", V_pu: 1.02, P_mw: 0, Q_mvar: 0 } },
  { id: "B2", type: "PQ", position: { x: 320, y: 60 },
    data: { id: "B2", kV: 13.8, type: "PQ", V_pu: 1.0, P_mw: 30, Q_mvar: 15 } },
  { id: "B3", type: "PQ", position: { x: 320, y: 200 },
    data: { id: "B3", kV: 13.8, type: "PQ", V_pu: 1.0, P_mw: 20, Q_mvar: 10 } },
];
const DEFAULT_EDGES = [
  { id: "L1", source: "B1", target: "B2",
    data: { R_pu: 0.01, X_pu: 0.05, B_pu: 0 }, ...edgeStyle("R 0.01 · X 0.05") },
  { id: "L2", source: "B2", target: "B3",
    data: { R_pu: 0.02, X_pu: 0.08, B_pu: 0 }, ...edgeStyle("R 0.02 · X 0.08") },
];

/* v3.0 · Convert an external buses/lines snapshot (e.g. a template's
   seeded inputs) into React-Flow nodes/edges. */
function busesToNodes(buses) {
  return (buses || []).map((b, i) => ({
    id: b.id, type: b.type,
    position: { x: 60 + (i % 3) * 220, y: 60 + Math.floor(i / 3) * 160 },
    data: { ...b },
  }));
}
function linesToEdges(lines) {
  return (lines || []).map((l, i) => ({
    id: `L${i + 1}`, source: l.from_bus, target: l.to_bus,
    data: { R_pu: l.R_pu ?? 0.01, X_pu: l.X_pu ?? 0.05, B_pu: l.B_pu ?? 0 },
    ...edgeStyle(`R ${l.R_pu ?? 0.01} · X ${l.X_pu ?? 0.05}`),
  }));
}

const BUS_FIELDS_BASE = [
  { key: "id",   label: "Bus ID",    type: "text" },
  { key: "kV",   label: "kV (L-L)",  type: "number", step: "0.1" },
  { key: "V_pu", label: "V pu",      type: "number", step: "0.01" },
];
const BUS_FIELDS_LOAD = [
  ...BUS_FIELDS_BASE,
  { key: "P_mw",   label: "P (MW)",   type: "number" },
  { key: "Q_mvar", label: "Q (MVAr)", type: "number" },
];
const LINE_FIELDS = [
  { key: "R_pu", label: "R (pu)", type: "number", step: "0.001" },
  { key: "X_pu", label: "X (pu)", type: "number", step: "0.001" },
  { key: "B_pu", label: "B (pu)", type: "number", step: "0.001" },
];

export function graphToTables(nodes, edges) {
  const buses = nodes.map((n) => ({
    id: n.data.id,
    kV: Number(n.data.kV),
    type: n.data.type,
    V_pu: Number(n.data.V_pu),
    P_mw: Number(n.data.P_mw || 0),
    Q_mvar: Number(n.data.Q_mvar || 0),
  }));
  const idOf = (nid) => nodes.find((n) => n.id === nid)?.data?.id || nid;
  const lines = edges.map((e) => ({
    from_bus: idOf(e.source), to_bus: idOf(e.target),
    R_pu: Number(e.data?.R_pu ?? 0.01),
    X_pu: Number(e.data?.X_pu ?? 0.05),
    B_pu: Number(e.data?.B_pu ?? 0),
  }));
  return { buses, lines };
}

function SLDEditorInner({ onChange, initialBuses, initialLines }) {
  const [nodes, setNodes, onNodesChange] = useNodesState(
    initialBuses ? busesToNodes(initialBuses) : DEFAULT_NODES);
  const [edges, setEdges, onEdgesChange] = useEdgesState(
    initialLines ? linesToEdges(initialLines) : DEFAULT_EDGES);
  const [sel, setSel] = useState({ node: null, edge: null });
  const idCounter = useRef(4);

  useEffect(() => { onChange?.(graphToTables(nodes, edges)); }, [nodes, edges, onChange]);

  const onConnect = useCallback((c) => {
    if (c.source === c.target) return;
    if (edges.some((e) =>
      (e.source === c.source && e.target === c.target) ||
      (e.source === c.target && e.target === c.source))) return;
    setEdges((es) => addEdge({
      ...c, id: `L${idCounter.current++}`,
      data: { R_pu: 0.01, X_pu: 0.05, B_pu: 0 }, ...edgeStyle("R 0.01 · X 0.05"),
    }, es));
  }, [edges, setEdges]);

  const addBus = (type) => {
    const id = `B${idCounter.current++}`;
    const defaults =
      type === "SLACK" ? { V_pu: 1.02, P_mw: 0, Q_mvar: 0 }
      : type === "PV"  ? { V_pu: 1.0,  P_mw: 20, Q_mvar: 0 }
      :                  { V_pu: 1.0,  P_mw: 10, Q_mvar: 5 };
    setNodes((ns) => [...ns, {
      id, type,
      position: { x: 140 + Math.random() * 360, y: 120 + Math.random() * 220 },
      data: { id, kV: 13.8, type, ...defaults },
    }]);
  };

  const clearAll = () => { setNodes([]); setEdges([]); setSel({ node: null, edge: null }); };

  const onSelectionChange = useCallback(({ nodes: ns, edges: es }) => {
    setSel({ node: ns[0] || null, edge: es[0] || null });
  }, []);

  const patchNode = (key, value) => {
    if (!sel.node) return;
    setNodes((all) => all.map((n) => n.id === sel.node.id
      ? { ...n, data: { ...n.data, [key]: value } } : n));
    setSel((s) => ({ ...s, node: { ...s.node, data: { ...s.node.data, [key]: value } } }));
  };
  const patchEdge = (key, value) => {
    if (!sel.edge) return;
    setEdges((all) => all.map((e) => e.id === sel.edge.id
      ? { ...e, data: { ...e.data, [key]: value },
          label: key === "R_pu" || key === "X_pu"
            ? `R ${key === "R_pu" ? value : e.data.R_pu} · X ${key === "X_pu" ? value : e.data.X_pu}`
            : e.label }
      : e));
    setSel((s) => ({ ...s, edge: { ...s.edge, data: { ...s.edge.data, [key]: value } } }));
  };
  const deleteSel = () => {
    if (sel.node) {
      const id = sel.node.id;
      setNodes((ns) => ns.filter((n) => n.id !== id));
      setEdges((es) => es.filter((e) => e.source !== id && e.target !== id));
    } else if (sel.edge) {
      const id = sel.edge.id;
      setEdges((es) => es.filter((e) => e.id !== id));
    }
    setSel({ node: null, edge: null });
  };

  const nodeFields = sel.node?.data?.type === "PQ" ? BUS_FIELDS_LOAD : BUS_FIELDS_BASE;

  return (
    <div className="h-[520px] border border-white/10 bg-[#0A0C10] relative" data-testid="sld-canvas">
      <Toolbar>
        <ToolbarBtn colour="#FFCC00" onClick={() => addBus("SLACK")} testid="sld-add-slack" title="Add slack bus">
          <Plus size={10} className="inline" /> slack
        </ToolbarBtn>
        <ToolbarBtn colour="#00E5FF" onClick={() => addBus("PQ")} testid="sld-add-pq" title="Add PQ bus">
          <Plus size={10} className="inline" /> pq
        </ToolbarBtn>
        <ToolbarBtn colour="#7CFF6B" onClick={() => addBus("PV")} testid="sld-add-pv" title="Add PV bus">
          <Plus size={10} className="inline" /> pv
        </ToolbarBtn>
        <ToolbarDivider />
        <ToolbarBtn colour="#FF3B30" onClick={deleteSel} testid="sld-delete-selected" title="Delete selected">
          <Trash size={10} className="inline" /> delete
        </ToolbarBtn>
        <ToolbarBtn colour="#8792A2" onClick={clearAll} testid="sld-clear" title="Reset">reset</ToolbarBtn>
      </Toolbar>

      <ReactFlow
        nodes={nodes} edges={edges}
        nodeTypes={nodeTypes}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onSelectionChange={onSelectionChange}
        fitView
        proOptions={{ hideAttribution: true }}
        colorMode="dark"
      >
        <Background gap={16} color="#ffffff08" />
        <Controls className="!bg-[#12151C] !border-white/10" showInteractive={false} />
        <MiniMap pannable zoomable
          nodeColor={(n) => (NODE_STYLE[n.type] || NODE_STYLE.PQ).colour}
          nodeStrokeWidth={2}
          maskColor="rgba(10,12,16,0.6)"
          className="!bg-[#12151C] !border !border-white/10" />
      </ReactFlow>

      {(sel.node || sel.edge) && (
        <SidePanel
          title={sel.node ? sel.node.data.id : `Line ${sel.edge.id}`}
          subtitle={sel.node ? `${sel.node.data.type} bus` : `${sel.edge.source} → ${sel.edge.target}`}
          node={sel.node} edge={sel.edge}
          fields={sel.node ? nodeFields : LINE_FIELDS}
          onChange={sel.node ? patchNode : patchEdge}
          onDelete={deleteSel}
          onClose={() => setSel({ node: null, edge: null })}
        />
      )}
    </div>
  );
}

export default function SLDEditor({ onChange, initialBuses, initialLines }) {
  return (
    <ReactFlowProvider>
      <SLDEditorInner onChange={onChange}
        initialBuses={initialBuses} initialLines={initialLines} />
    </ReactFlowProvider>
  );
}
